'use client'

import { memo } from 'react'
import { Card } from '@heroui/react'
import { EmptyState, NumberValue } from '@heroui-pro/react'
import { BriefcaseBusiness } from 'lucide-react'
import type { Deal } from '../../lib/types'
import { formatMoney, titleCase } from '../../lib/view'

const MIX_COLORS = ['var(--chart-3)', 'var(--chart-2)', 'var(--chart-4)', 'var(--chart-1)']

interface PortfolioMixPoint extends Record<string, number | string> {
  count: number
  name: string
  value: number
}

function getPortfolioMix(deals: Deal[]): PortfolioMixPoint[] {
  const byType = new Map<string, PortfolioMixPoint>()

  for (const deal of deals) {
    const name = titleCase(deal.financing_type || 'Unspecified')
    const current = byType.get(name) ?? { count: 0, name, value: 0 }

    current.count += 1
    current.value += deal.purchase_price ?? 0
    byType.set(name, current)
  }

  const sorted = [...byType.values()].sort((a, b) => b.value - a.value || b.count - a.count)

  if (sorted.length <= 4) return sorted

  const visible = sorted.slice(0, 3)
  const other = sorted.slice(3).reduce<PortfolioMixPoint>(
    (sum, item) => ({
      count: sum.count + item.count,
      name: 'Other',
      value: sum.value + item.value,
    }),
    { count: 0, name: 'Other', value: 0 },
  )

  return [...visible, other]
}

function formatCompactMoney(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`
  return formatMoney(value)
}

export const PortfolioMixCard = memo(function PortfolioMixCard({ deals, isLoading }: { deals: Deal[]; isLoading: boolean }): React.ReactElement {
  const mix = getPortfolioMix(deals)
  const total = mix.reduce((sum, item) => sum + item.value, 0)

  return (
    <Card>
      <Card.Header>
        <Card.Title className="text-base font-semibold text-foreground">Financing Allocation</Card.Title>
        <Card.Description className="text-sm text-muted">Financing share by purchase value</Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        {isLoading || mix.length === 0 || total === 0 ? (
          <EmptyState className="min-h-[280px] w-full rounded-lg bg-surface-secondary" size="sm">
            <EmptyState.Header>
              <EmptyState.Media variant="icon">
                <BriefcaseBusiness className="size-5" />
              </EmptyState.Media>
              <EmptyState.Title>No Portfolio Mix</EmptyState.Title>
              <EmptyState.Description>Deal values will populate this view.</EmptyState.Description>
            </EmptyState.Header>
          </EmptyState>
        ) : (
          <>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted">Allocated value</span>
              <NumberValue
                className="text-2xl font-semibold tabular-nums text-foreground"
                currency="USD"
                maximumFractionDigits={1}
                notation="compact"
                style="currency"
                value={total}
              />
            </div>
            <div aria-label="Financing allocation" className="flex h-3 w-full overflow-hidden rounded-full bg-surface-secondary" role="img">
              {mix.map((entry, index) => (
                <span
                  key={entry.name}
                  aria-label={`${entry.name} ${((entry.value / total) * 100).toFixed(1)}%`}
                  className="h-full"
                  style={{
                    backgroundColor: MIX_COLORS[index % MIX_COLORS.length],
                    width: `${(entry.value / total) * 100}%`,
                  }}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {mix.map((entry, index) => {
                const pct = ((entry.value / total) * 100).toFixed(1)

                return (
                  <div key={entry.name} className="flex items-center justify-between gap-3 text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: MIX_COLORS[index % MIX_COLORS.length] }}
                      />
                      <span className="truncate font-medium text-foreground">{entry.name}</span>
                    </div>
                    <div className="flex shrink-0 items-baseline gap-3 tabular-nums">
                      <span className="text-xs text-muted">{pct}%</span>
                      <span className="text-sm font-medium text-foreground">{formatCompactMoney(entry.value)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </Card.Content>
    </Card>
  )
})
